import React from 'react';
import Moment from 'react-moment';
import axios from 'axios';
import { withStyles} from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import Button from '@material-ui/core/Button';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import confirm from '../../shared/ConfirmDialog';

const styles = theme => ({
  root: {
    display: 'block',
    width: '100%',
    border: '1px solid #f5f5f5',
    borderRadius: 5,
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    '-ms-overflow-style': '-ms-autohiding-scrollbar',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& thead tr th': {
      padding: 10,
      textAlign: 'center',
      fontSize: 12,
      border: '1px solid #f5f5f5',
      cursor: 'pointer',
    },
    '& tbody tr td': {
      padding: 10,
      border: '1px solid #f5f5f5',
    },
    '& tbody tr:nth-of-type(odd) td': {
      backgroundColor: '#fafafa',
    },
  },
  tableHeadingText: {
    lineHeight: 2,
    '& b': {
      verticalAlign: 'super',
    },
  },
  textCenter: {
    textAlign: 'center',
  },
  textMute: {
    color: '#999999',
  },
});

class AbsencesTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sort: null,
      sortDirection: null,
    };

    this.columns = [
      {
        key: 'date_absent',
        headingText: props.t('Date'),
        content: (entry) => {
          return (
            <React.Fragment>
              <Moment format={'ddd ' + dateFormat}>{entry.date_absent}</Moment>
            </React.Fragment>
          );
        },
      },
      {
        key: 'reason',
        headingText: props.t('Reason for absence'),
        content: (entry) => {
          return (
            <React.Fragment>
              {entry.reason}
            </React.Fragment>
          );
        },
      },
      {
        key: 'seen_by_teacher',
        headingText: props.t('Acknowledged by'),
        content: (entry) => {
          return (
            <React.Fragment>
              {entry.seen_by_teacher && (
                <span>{entry.seen_by_teacher} </span>
              )}
              {entry.date_seen && (
                <span>
                  (<Moment format={dateFormat + ' HH:mm'}>{entry.date_seen}</Moment>)
                </span>
              )}
            </React.Fragment>
          );
        },
      },
      {
        key: 'actions',
        headingText: props.t('Actions'),
        content: (entry) => {
          if (!entry.can_edit) {
            return null;
          }

          return (
            <React.Fragment>
              <Button
                color="inherit"
                size="small"
                onClick={this.onEditClick.bind(this, entry)}
                className="edit-button"
              >
                {props.t('Edit')}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={this.onDeleteClick.bind(this, entry)}
                className="delete-button"
              >
                {props.t('Delete')}
              </Button>
            </React.Fragment>
          );
        },
      },
    ];

    this.onTableHeadingClick = this.onTableHeadingClick.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.getFinalEntries = this.getFinalEntries.bind(this);
  }

  onTableHeadingClick(column) {
    const {
      sort,
      sortDirection,
    } = this.state;

    let newSort = column.key;
    let newSortDirection = sortDirection;

    if (!newSortDirection) {
      newSortDirection = 'asc';
    }

    if (newSort === sort) {
      newSortDirection = newSortDirection === 'asc'
        ? 'desc'
        : 'asc';
    }

    this.setState({
      sort: newSort,
      sortDirection: newSortDirection,
    });
  }

  onEditClick(entry) {
    this.props.onEditEntryClick(entry);
  }

  onDeleteClick(entry) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    confirm(t('Are you sure you wish to remove this report?')).then(
      () => {
        axios
          .post(
            `${window.apiReportAbsenceUrl}/deletereportedabsence/${entry.id}`,
            {},
            {
              cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
            }
          ).then((response) => {
            if (response.data.error) {
              enqueueSnackbar(response.data.error, {
                variant: 'error',
                autoHideDuration: 2000,
              });
            } else {
              enqueueSnackbar(t('You have successfully deleted the report'), {
                variant: 'success',
                autoHideDuration: 2000,
              });

              this.props.reloadData();
            }
          }).catch((thrown) => {});
      },
      () => {
        // Nothing to do
      }
    );
  }

  getFinalEntries(entries) {
    const {
      sort,
      sortDirection,
    } = this.state;

    let finalEntries = [ ...entries ];

    finalEntries.sort((a, b) => {
      if (sort === 'date_absent') {
        return a[sort] === b[sort]
          ? 0
          : (a[sort] > b[sort]
            ? 1
            : -1
          );
      }

      if (a[sort] && b[sort]) {
        return a[sort].localeCompare(b[sort]);
      }

      return 0;
    });

    if (sortDirection === 'desc') {
      finalEntries = finalEntries.reverse();
    }

    return finalEntries;
  }

  render() {
    const {
      classes,
      entries,
    } = this.props;
    const {
      sort,
      sortDirection,
    } = this.state;

    const finalEntries = this.getFinalEntries(entries);

    return (
      <div className={`absences-table ${classes.root}`}>
        <table className={classes.table}>
          <thead>
            <tr>
              {this.columns.map((column) => {
                let columnProps = {};
                if (column.headerWidth) {
                  columnProps.width = column.headerWidth;
                }

                return (
                  <th
                    { ...columnProps }
                    key={column.key}
                    onClick={this.onTableHeadingClick.bind(this, column)}
                  >
                    <div className={classes.tableHeadingText}>
                      <b>{column.headingText}</b>
                      {(column.key !== sort || !sortDirection) && column.key !== 'actions' && (
                        <span>
                          <SwapVertIcon className={classes.textMute} fontSize="small" />
                        </span>
                      )}
                      {column.key === sort && sortDirection && column.key !== 'actions' && (
                        <span>
                          {sortDirection === 'asc' && (
                            <ArrowUpwardIcon fontSize="small" />
                          )}
                          {sortDirection === 'desc' && (
                            <ArrowDownwardIcon fontSize="small" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {finalEntries.map((entry) => {
              return (
                <tr key={entry.id}>
                  {this.columns.map((column) => {
                    return (
                      <td key={column.key}>
                        {column.content.bind(this, entry)()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(withSnackbar(AbsencesTable)));
